import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { Textarea } from "../ui/textarea";
import { useCreateBibliography } from "@/api/bibliography/bibliography";
import {
  appendNoDuplicateBibliographyNumbersRefine,
  bibliographySchema,
} from "@/api/bibliography/bibliography-validation";

const schema = appendNoDuplicateBibliographyNumbersRefine(
  bibliographySchema.pick({
    bibliographieDescriptionNumeroBibliographie: true,
    bibliographieDescriptionTitre: true,
  }),
);
type FormType = z.infer<typeof schema>;

type NewBibliographyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewBibliographyDialog = ({
  onOpenChange,
  open,
}: NewBibliographyDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: {
      bibliographieDescriptionNumeroBibliographie: "",
      bibliographieDescriptionTitre: "",
    },
  });
  const { mutateAsync } = useCreateBibliography();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType, shouldRedirect: boolean) => {
    try {
      const result = await mutateAsync(data);
      if (!shouldRedirect) {
        toast({
          title: t("newRecordSuccessfullyCreated"),
          duration: 3000,
          variant: "success",
        });
        form.reset();
        return;
      }
      navigate(`/edit/bibliography/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("bibliographieDescriptionNumeroBibliographie", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createBbibliography")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="bibliographieDescriptionNumeroBibliographie"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("bibliographieDescriptionNumeroBibliographie")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="bibliographieDescriptionTitre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("bibliographieDescriptionTitre")}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewBibliographyDialog;
